import React, { useState } from 'react';
import { tokenizeCitations } from './citationTokens';
import { SourceCitation, TranscriptCitation } from './CitationButtons';

const inlineMarkdown = (line) => line
  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
  .replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
  .replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">$1</code>');

// A GFM table's second line, e.g. |---|:--:|. The pipe is required so a
// setext underline or a horizontal rule is not mistaken for a table.
const TABLE_DIVIDER = /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?$/;
const isTableDivider = (line) => line.includes('|') && TABLE_DIVIDER.test(line);

// A horizontal rule. Headings already separate a report's sections, but a
// generated one often draws a rule anyway, and unrendered it reads as "---".
const HORIZONTAL_RULE = /^(?:-{3,}|\*{3,}|_{3,})$/;

const splitTableRow = (line) => line
  .trim()
  .replace(/^\|/, '')
  .replace(/\|$/, '')
  .split('|')
  .map((cell) => cell.trim());

const columnAlignments = (divider) => splitTableRow(divider).map((cell) => {
  const left = cell.startsWith(':');
  const right = cell.endsWith(':');
  if (left && right) return 'center';
  return right ? 'right' : 'left';
});

const HEADINGS = [
  ['### ', 'h3', 'text-lg font-semibold mb-2 mt-4 text-gray-900'],
  ['## ', 'h2', 'text-xl font-semibold mb-3 mt-5 text-gray-900'],
  ['# ', 'h1', 'text-2xl font-bold mb-4 mt-6 text-gray-900'],
];

const LIST_CLASS = 'list-disc list-inside mb-4 space-y-1 ml-4';
const PARAGRAPH_CLASS = 'mb-4 text-base leading-7 text-gray-800';

const ChevronIcon = ({ d }) => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
  </svg>
);

/**
 * Markdown (headings, lists, tables, rules, bold/italic/code) with [N] / [N-M] transcript
 * citations and [Sn] source citations as buttons. Text longer than `maxLength` is cut
 * with a "Read more" toggle.
 */
export default function CitedMarkdown({ text, maxLength = 500, className = '', transcriptReferences, sourceReferences, onCitationClick }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text || (typeof text === 'string' && text.trim().length === 0)) {
    return (
      <div className={`text-gray-500 italic ${className}`}>
        No content available
      </div>
    );
  }

  const textStr = String(text).replace(/[\u2011\u2013\u2014]/g, '-');
  const shouldTruncate = textStr.length > maxLength;
  const displayText = isExpanded || !shouldTruncate ? textStr : `${textStr.substring(0, maxLength)}...`;

  const hasCitations = (transcriptReferences && onCitationClick) || sourceReferences;
  const isCited = (value) => hasCitations && (
    /\[\d+(?:-\d+)?\]/.test(value)
    || /【\d+(?:-\d+)?】/.test(value)
    || /\[S\d+\]/.test(value)
    || (sourceReferences && /\bS\d+\b/.test(value))
  );

  const renderWithCitations = (html, keyPrefix) => {
    const tokens = tokenizeCitations(html, { withSources: Boolean(sourceReferences) });
    if (tokens.length === 1 && tokens[0].type === 'text') {
      return <span dangerouslySetInnerHTML={{ __html: html }} />;
    }
    return tokens.map((token, i) => {
      if (token.type === 'source') {
        return (
          <SourceCitation
            key={`${keyPrefix}-src-${i}`}
            num={token.num}
            reference={sourceReferences ? sourceReferences[String(token.num)] : null}
          />
        );
      }
      if (token.type === 'transcript') {
        return (
          <TranscriptCitation
            key={`${keyPrefix}-cit-${i}`}
            token={token}
            transcriptReferences={transcriptReferences}
            onCitationClick={onCitationClick}
          />
        );
      }
      return <span key={`${keyPrefix}-txt-${i}`} dangerouslySetInnerHTML={{ __html: token.content }} />;
    });
  };

  const renderCell = (raw, key) => {
    const html = inlineMarkdown(raw);
    if (isCited(html)) return renderWithCitations(html, key);
    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  };

  /** One element per line, or per block of lines for tables and lists. */
  const formatMarkdown = (markdown) => {
    const lines = markdown.split('\n');
    const elements = [];
    let listItems = [];
    // A table spans several lines, so the rows it swallows are skipped here.
    let skipTo = 0;

    const flushList = (index) => {
      if (listItems.length === 0) return;
      elements.push(<ul key={`ul-${index}`} className={LIST_CLASS}>{listItems}</ul>);
      listItems = [];
    };

    const renderTable = (index, trimmed) => {
      const header = splitTableRow(trimmed);
      const aligns = columnAlignments(lines[index + 1]);
      const rows = [];
      let cursor = index + 2;
      while (cursor < lines.length && lines[cursor].includes('|')) {
        rows.push(splitTableRow(lines[cursor]));
        cursor += 1;
      }
      skipTo = cursor;
      return (
        <div key={`table-${index}`} className="overflow-x-auto mb-4">
          <table className="min-w-full text-sm border-collapse">
            <thead>
              <tr>
                {header.map((cell, col) => (
                  <th
                    key={`th-${col}`}
                    className="border border-gray-300 bg-gray-50 px-3 py-2 font-semibold text-gray-900"
                    style={{ textAlign: aligns[col] || 'left' }}
                  >
                    {renderCell(cell, `th-${index}-${col}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={`tr-${rowIndex}`}>
                  {header.map((_, col) => (
                    <td
                      key={`td-${col}`}
                      className="border border-gray-300 px-3 py-2 align-top text-gray-800"
                      style={{ textAlign: aligns[col] || 'left' }}
                    >
                      {renderCell(row[col] || '', `td-${index}-${rowIndex}-${col}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    };

    lines.forEach((line, index) => {
      if (index < skipTo) return;
      const trimmed = line.trim();

      if (trimmed.includes('|') && lines[index + 1] && isTableDivider(lines[index + 1].trim())) {
        flushList(index);
        elements.push(renderTable(index, trimmed));
        return;
      }

      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        const html = inlineMarkdown(trimmed.substring(2));
        listItems.push(isCited(html)
          ? <li key={`li-${index}`} className="mb-1">{renderWithCitations(html, `li-${index}`)}</li>
          : <li key={`li-${index}`} className="mb-1" dangerouslySetInnerHTML={{ __html: html }} />);
        return;
      }

      flushList(index);
      const heading = HEADINGS.find(([prefix]) => trimmed.startsWith(prefix));
      if (HORIZONTAL_RULE.test(trimmed)) {
        elements.push(<hr key={`hr-${index}`} className="my-6 border-gray-200" />);
      } else if (heading) {
        const [prefix, Tag, headingClass] = heading;
        elements.push(<Tag key={`${Tag}-${index}`} className={headingClass}>{trimmed.replace(prefix, '')}</Tag>);
      } else if (trimmed === '') {
        elements.push(<br key={`br-${index}`} />);
      } else {
        const html = inlineMarkdown(trimmed);
        elements.push(isCited(html)
          ? <p key={`p-${index}`} className={PARAGRAPH_CLASS}>{renderWithCitations(html, `p-${index}`)}</p>
          : <p key={`p-${index}`} className={PARAGRAPH_CLASS} dangerouslySetInnerHTML={{ __html: html }} />);
      }
    });

    if (listItems.length > 0) {
      elements.push(<ul key="ul-final" className={LIST_CLASS}>{listItems}</ul>);
    }
    return elements;
  };

  return (
    <div className={className}>
      <div className="prose prose-sm max-w-none text-gray-800 leading-relaxed break-words">
        {formatMarkdown(displayText)}
      </div>
      {shouldTruncate && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
        >
          {isExpanded ? (
            <>
              <span>Read less</span>
              <ChevronIcon d="M5 15l7-7 7 7" />
            </>
          ) : (
            <>
              <span>Read more</span>
              <ChevronIcon d="M19 9l-7 7-7-7" />
            </>
          )}
        </button>
      )}
    </div>
  );
}
